import { customerOrganisation } from "../enterpriseCustomer/enterpriseCustomer";
import { DynamicChip } from "../dynamicShared/DynamicChip";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Translate, useTranslation } from "@privacy/translations";
import { useCurrentCustomer } from "../root/CurrentOrganisationProvider";
import { Strong } from "./Strong";
import { STATUS_WORDS } from "./StatusWord";
import type { SharedRegistrationEvent, SharedRegistrationHistoryEntry } from "../api/registrations.types";
import type { BaseHistoryEntry } from "../dynamicHistory/types";
import type { Form } from "../dynamicForm/types";

const SHARED_REGISTRATION_EVENT_KEYS = {
  sharedRegistrationAddedToOrganisation: "SharedProcessing.History.AddedToOrganisation",
  sharedRegistrationRemovedFromOrganisation: "SharedProcessing.History.RemovedFromOrganisation",
  sharedRegistrationWithdrawnFromLibrary: "SharedProcessing.History.WithdrawnFromLibrary",
  sharedRegistrationLeftCustomer: "SharedProcessing.History.LeftCustomer",
  sharedRegistrationChangedByCustomer: "SharedProcessing.History.ApprovedNewVersion",
  sharedRegistrationClaimedByOrganisation: "SharedProcessing.History.ClaimedByOrganisation",
  sharedRegistrationOwnCopyKeptByOrganisation: "SharedProcessing.History.OwnCopyKeptByOrganisation",
  sharedRegistrationDeclinedByOrganisation: "SharedProcessing.History.DeclinedByOrganisation",
} as const satisfies Record<SharedRegistrationHistoryEntry["customType"], string>;

export const isSharedRegistrationEvent = <E extends { customType: string }>(
  entry: E
): entry is Extract<E, SharedRegistrationHistoryEntry> => entry.customType in SHARED_REGISTRATION_EVENT_KEYS;

export const SharedRegistrationEventEntry = ({
  entry,
  form,
}: {
  entry: BaseHistoryEntry & SharedRegistrationHistoryEntry;
  form?: Form;
}) => {
  const { createdAt, createdBy, organisationId } = entry;
  const event: Partial<SharedRegistrationEvent> =
    entry.customType === "sharedRegistrationAddedToOrganisation"
      ? { organisationName: entry.newValue?.name }
      : (entry.newValue ?? {});
  const { byName, organisationName, comment, fields, fieldQuestionIds } = event;
  const headers = new Map(
    form?.sections.flatMap(section => section.questionCards.map(card => [card.id, card.header] as const))
  );
  const readerFields = fields?.map((field, index) => headers.get(fieldQuestionIds?.[index] ?? "") ?? field);
  const translationKey = SHARED_REGISTRATION_EVENT_KEYS[entry.customType];
  const { t, formatDate, formatList } = useTranslation();
  const currentCustomer = useCurrentCustomer();
  const unit =
    organisationId === currentCustomer.id
      ? currentCustomer.name
      : (customerOrganisation(currentCustomer, organisationId)?.name ?? byName);

  const resolvedKey =
    translationKey === "SharedProcessing.History.RemovedFromOrganisation" && organisationId === event.organisationId
      ? "SharedProcessing.History.RemovedByOrganisation"
      : translationKey;

  return (
    <Stack>
      <Typography
        sx={{ fontSize: theme => theme.typography.pxToRem(12), mb: 0.5, color: theme => theme.palette.text.tertiary }}
      >
        {formatDate(createdAt, { showTime: true })}
      </Typography>
      <Typography sx={{ fontWeight: 600, fontSize: theme => theme.typography.pxToRem(14) }}>
        {createdBy.firstName} {createdBy.lastName}
        {unit && (
          <Typography component="span" sx={{ fontSize: "inherit", color: theme => theme.palette.text.tertiary }}>
            {` (${unit})`}
          </Typography>
        )}
        <Typography component="span" sx={{ fontWeight: 400, fontSize: "inherit" }}>
          {" "}
          <Translate
            translationKey={resolvedKey}
            values={{
              organisation: <Strong>{organisationName ?? ""}</Strong>,
              customer: byName ?? "",
              fields: formatList(readerFields ?? []),
              ...STATUS_WORDS,
            }}
          />
        </Typography>
      </Typography>
      {entry.customType === "sharedRegistrationChangedByCustomer" && readerFields && readerFields.length > 0 && (
        <Stack direction="row" sx={{ mt: 2, gap: 1, flexWrap: "wrap" }}>
          {readerFields.map(field => (
            <DynamicChip variant="choice" key={field} label={field} />
          ))}
        </Stack>
      )}
      {comment && (
        <Typography
          sx={{
            mt: 2,
            fontSize: theme => theme.typography.pxToRem(14),
            fontStyle: "italic",
            color: theme => theme.palette.text.secondary,
            overflowWrap: "anywhere",
            whiteSpace: "pre-line",
          }}
        >
          {t("RegistrationGrid.Drawer.OwnCopyWarning.Reason", { comment })}
        </Typography>
      )}
    </Stack>
  );
};
